import { memo } from 'react'
import { generatePath } from 'react-router-dom'
import clsx from 'clsx'

import { Avatar, Box, IconButton, LinearProgress, makeStyles, Typography } from '@material-ui/core'
import GifIcon from '@material-ui/icons/Gif'

import { Carousel } from 'UI/Components'
import { TrackLink } from './TrackLink'
import { CopyEmbedCode } from './CopyEmbedCode'

import {
    useQuery,
    useNotification,
    useCopyLinkedGif,
    useAuth,
    useCopyToClipboard,
    copyToClipboardMessage
} from 'hooks'

import { PlaybookType } from 'app/types'
import { paths } from 'app/paths'
import { copyToClipboard, isQG } from 'modules'
import { firebaseConfig } from 'env'

type LinkAppType = {
    enableTrackedLink: boolean
    favicon: string
    logo: string
    recipientEmailVariable: string
    senderEmailVariable: string
    title: string
}

const useStyles = makeStyles(theme => ({
    flexColumn: {
        display: 'flex',
        justifyContent: 'start',
        alignItems: 'center',
        flexDirection: 'column'
    },
    avatar: {
        width: '30px',
        height: '30px',
        fontSize: '14px'
    },
    avatarBackground: {
        background: theme.palette.secondary.main
    }
}))

type Props = {
    playbook: PlaybookType
    startAt?: number
    appsOnly?: boolean
}

export const LinkTemplates = memo(({ appsOnly, startAt, playbook }: Props) => {
    const classes = useStyles()

    const { isPublic, id, title, isPlaylist, gifUrl, screenshotUrl } = playbook

    const { uid } = useAuth()

    const { copyLinkedGif } = useCopyLinkedGif()

    const { copyToClipboard: copyToClipboardByHook } = useCopyToClipboard()

    const { showErrorNotification, showWarningNotification, showSuccessNotification } =
        useNotification()

    const { data, isValidating } = useQuery<{
        data: Array<LinkAppType>
    }>(
        `/b/v1/trackLinkApps`,
        { method: 'GET' },
        {
            onError: () => showErrorNotification(copyToClipboardMessage),
            revalidateOnFocus: false
        }
    )

    const linkApps = data?.data || []

    const copyServiceNow = () => {
        const { id, isPublic, isPlaylist, title } = playbook

        const playbookPath = generatePath(isPublic ? paths.publicPlaybook : paths.playbookDetails, {
            playbookId: id
        })
        const playlistPath = generatePath(isPublic ? paths.publicPlaylist : paths.playlistDetails, {
            playbookId: id
        })

        const path = isPlaylist ? playlistPath : playbookPath

        const href = `https://${firebaseConfig.authDomain}${path}?origin=${uid}`
        const copyData = `[code]<a href="${href}">${title.trim()}</a>[/code]`

        copyToClipboard(copyData).then(() => showSuccessNotification(copyToClipboardMessage))
    }

    const handleLinkClick = (link: LinkAppType) => {
        const { senderEmailVariable: sender, recipientEmailVariable: recipient } = link

        const playbookPath = generatePath(paths.publicPlaybook, {
            playbookId: id
        })

        const playlistPath = generatePath(paths.publicPlaylist, {
            playbookId: id
        })

        const path = isPlaylist ? playlistPath : playbookPath
        const root = window.location.origin + path

        const width = isQG(playbook) ? '700px' : '250px'
        const height = isQG(playbook) ? 'auto' : '175px'

        let linkTemplate = `${root}?track_link_name=${recipient}&origin=${sender || uid}`

        if (startAt) {
            linkTemplate += `&startAt=${startAt}`
        }

        const imageHtml = `
                <table bgcolor="white" style="background-color: white" width="100%">
                  <tr>
                    <td>
                    <a style="text-decoration: underline" href=${linkTemplate}>${playbook.title}</a>
                    </td>
                  </tr>
                   <tr>
                    <td>
                      <a href=${linkTemplate}>
                        <img width=${width} height=${height} alt=""
                        style={{width,height,objectFit:"contain",borderRadius:"2px"}} src=${
                            gifUrl || screenshotUrl
                        } />
                      </a>
                    </td>
                  </tr>
                </table>`

        copyToClipboardByHook(imageHtml)
    }

    if (isValidating) return <LinearProgress />

    return (
        <Box position="relative">
            <Carousel spacing={1.5}>
                {!appsOnly && (
                    <>
                        <Box className={classes.flexColumn}>
                            <IconButton
                                title="Copy linked GIF"
                                size="small"
                                onClick={() => copyLinkedGif(playbook)}
                                style={{ background: 'black' }}
                            >
                                <GifIcon style={{ color: 'white' }} />
                            </IconButton>
                            <Typography variant="body2">GIF</Typography>
                        </Box>
                        <TrackLink playbook={playbook} disabled={!isPublic} />
                        <CopyEmbedCode
                            disabled={!isPublic}
                            playbookId={id}
                            playbookTitle={title}
                            isPlaylist={isPlaylist}
                        />
                    </>
                )}

                {linkApps.map((link, idx: number) => (
                    <Box key={idx} className={classes.flexColumn}>
                        <IconButton
                            size="small"
                            onClick={() => {
                                if (!isPublic) {
                                    return showWarningNotification(
                                        'The video privacy settings require you to share it with anyone in order to copy tracked link'
                                    )
                                }
                                // ServiceNow have a specific format to copy
                                if (link.title === 'ServiceNow') {
                                    copyServiceNow()
                                    return
                                }
                                handleLinkClick(link)
                            }}
                            style={{
                                width: '30px',
                                height: '30px',
                                opacity: isPublic ? 1 : 0.5
                            }}
                        >
                            <Avatar
                                src={link.favicon || link.logo}
                                alt={link.title}
                                className={clsx(classes.avatar, {
                                    [classes.avatarBackground]: !link.favicon && !link.logo
                                })}
                            >
                                <Box title={link.title} className={classes.avatarBackground}>
                                    {link.title.charAt(0).toUpperCase()}
                                </Box>
                            </Avatar>
                        </IconButton>
                        <Typography variant="body2">{link.title}</Typography>
                    </Box>
                ))}
            </Carousel>
        </Box>
    )
})
